import React, { useEffect, useRef, useState } from "react";
import { useIsFetching, useIsMutating } from "react-query";

/**
 * A thin progress bar pinned to the top of the content area that is shown
 * whenever ANY react-query request (query or mutation) is in flight. This
 * gives the user consistent feedback every time the admin UI loads data,
 * without wiring loading state into each individual page.
 *
 * It hooks into the global query cache via useIsFetching/useIsMutating, so it
 * automatically covers every query and mutation in the app.
 */
const GlobalLoadingBar: React.FC = () => {
  const activeRequests = useIsFetching() + useIsMutating();
  const isActive = activeRequests > 0;

  // Keep the bar mounted briefly after the last request finishes so the
  // fade-out/complete animation can play instead of snapping away.
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isActive) {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      setVisible(true);
    } else if (visible) {
      hideTimer.current = setTimeout(() => setVisible(false), 400);
    }
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [isActive, visible]);

  if (!visible) return null;

  return (
    <div
      role="progressbar"
      aria-busy={isActive}
      aria-label="Loading"
      className="pointer-events-none absolute inset-x-0 top-0 z-50 h-[3px] overflow-hidden"
    >
      <style>{`
        @keyframes pcg-loading-indeterminate {
          0%   { left: -40%; width: 40%; }
          50%  { left: 30%;  width: 55%; }
          100% { left: 100%; width: 40%; }
        }
      `}</style>
      <div
        className="absolute top-0 h-full rounded-full"
        style={{
          background:
            "linear-gradient(90deg, rgba(209,169,111,0) 0%, rgb(var(--pcg-gold-dark)) 50%, rgba(209,169,111,0) 100%)",
          animation: "pcg-loading-indeterminate 1.1s ease-in-out infinite",
          opacity: isActive ? 1 : 0,
          transition: "opacity 0.35s ease-out",
        }}
      />
    </div>
  );
};

export default GlobalLoadingBar;
